import React, { useState, useCallback, useEffect } from 'react';
import { ThemeToggle } from './theme';
import Icon from './icons';

const API_URL = process.env.REACT_APP_API_URL || '/api';
// The redeemed key is the only credential, so persisting it keeps the customer
// "logged in" across refreshes and reopened tabs until they use another key.
const STORAGE_KEY = 'redeemKey';

// Fallback tutorial shown when the admin hasn't set custom content.
const DEFAULT_TUTORIAL = [
    'We provide one active email address only. Complete the registration yourself and select United States as the country/region. If Adobe requests a verification code, click "Get Code" on our website to receive it by email.',
    'If your Adobe account becomes unavailable, visit our website and click "Account Abnormal? Change Account" to receive a new valid account.',
    'Once your key expires, this account is automatically released and can no longer be accessed with this key — it may be reassigned to a new customer. Please redeem a new key before yours expires if you want to keep using the service.',
];

// Public account-distribution page. A customer enters their card key and gets
// the account bound to it.
function RedeemKey() {
    const [key, setKey] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [copied, setCopied] = useState('');
    const [switching, setSwitching] = useState(false);
    const [notice, setNotice] = useState(null);
    const [otp, setOtp] = useState({ code: '------', status: 'idle', message: '' });
    const [otpLoading, setOtpLoading] = useState(false);
    const [otpCooldown, setOtpCooldown] = useState(0);
    // True while we restore a saved session on first load, so we don't flash the
    // key-entry form before the stored key resolves.
    const [restoring, setRestoring] = useState(() => !!localStorage.getItem(STORAGE_KEY));

    const post = useCallback(async (path, body, options = {}) => {
        const timeoutMs = Number(options.timeoutMs || 0);
        const controller = timeoutMs > 0 ? new AbortController() : null;
        const timer = timeoutMs > 0 ? setTimeout(() => controller.abort(), timeoutMs) : null;
        try {
            const res = await fetch(`${API_URL}${path}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
                signal: controller?.signal,
            });
            const text = await res.text();
            let parsed = null;
            try {
                parsed = text ? JSON.parse(text) : null;
            } catch {
                parsed = null;
            }
            return {
                ok: res.ok,
                body: parsed || { success: false, message: text || res.statusText || 'Request failed' },
            };
        } catch (err) {
            if (err?.name === 'AbortError') {
                throw new Error('REQUEST_TIMEOUT');
            }
            throw err;
        } finally {
            if (timer) clearTimeout(timer);
        }
    }, []);

    // On load, restore a previously-redeemed key. If it's since expired, banned,
    // or gone, clear it and fall back to the entry form.
    useEffect(() => {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (!saved) return;
        let cancelled = false;
        (async () => {
            try {
                const { ok, body } = await post('/redeem/status', { key: saved });
                if (cancelled) return;
                if (ok && body.success) {
                    setKey(saved);
                    setData(body.data);
                } else {
                    localStorage.removeItem(STORAGE_KEY);
                }
            } catch {
                // Network hiccup — keep the saved key so a later reload can retry.
            } finally {
                if (!cancelled) setRestoring(false);
            }
        })();
        return () => { cancelled = true; };
    }, [post]);

    const handleRedeem = async (e) => {
        e.preventDefault();
        if (!key.trim()) return;
        setLoading(true);
        setError(null);
        setNotice(null);
        try {
            const { ok, body } = await post('/redeem/login', { key: key.trim() });
            if (ok && body.success) {
                setData(body.data);
                localStorage.setItem(STORAGE_KEY, key.trim());
            } else {
                setError(body.message || 'Something went wrong. Please try again.');
            }
        } catch {
            setError('Could not reach the server. Please try again.');
        }
        setLoading(false);
    };

    const handleSwitch = async () => {
        if (switching) return;
        setSwitching(true);
        setError(null);
        setNotice(null);
        try {
            const { ok, body } = await post('/redeem/refresh', { key: data.key });
            if (ok && body.success) {
                setData(body.data);
                // A switch invalidates any previously fetched code for the old account.
                setOtp({ code: '------', status: 'idle', message: '' });
                setOtpCooldown(0);
                setNotice('A new account has been assigned. Use the new email and password above.');
            } else {
                setNotice(body.message || 'No other account is available right now. Please try again later.');
            }
        } catch {
            setNotice('Could not reach the server. Please try again.');
        }
        setSwitching(false);
    };

    const handleGetOtp = async () => {
        if (otpLoading || otpCooldown > 0 || !data?.key) return;
        setOtpLoading(true);
        setOtp({ code: 'Wait...', status: 'waiting', message: 'Checking for a fresh email code. This can take up to 60 seconds.' });
        try {
            const { ok, body } = await post('/redeem/get-code', { key: data.key }, { timeoutMs: 70000 });
            if (ok && body.success) {
                setOtp({ code: body.code || '------', status: 'success', message: 'Verification code fetched.' });
                setOtpCooldown(60);
            } else {
                const message = body.message || 'Could not fetch verification code.';
                const noFreshCode = /expired|no recent|send a new code|resend code|could not find a recent/i.test(message);
                setOtp({
                    code: noFreshCode ? 'No fresh code' : 'Fail',
                    status: noFreshCode ? 'waiting' : 'error',
                    message,
                });
            }
        } catch (err) {
            setOtp({
                code: 'Error',
                status: 'error',
                message: err?.message === 'REQUEST_TIMEOUT'
                    ? 'The verification-code service took too long. Send a new code and try again.'
                    : 'Could not reach the server. Please try again.',
            });
        }
        setOtpLoading(false);
    };

    React.useEffect(() => {
        if (otpCooldown <= 0) return undefined;
        const timer = setInterval(() => {
            setOtpCooldown((seconds) => Math.max(0, seconds - 1));
        }, 1000);
        return () => clearInterval(timer);
    }, [otpCooldown]);

    const copy = async (text, label) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(label);
            setTimeout(() => setCopied(''), 1500);
        } catch {
            /* clipboard unavailable — ignore */
        }
    };

    const reset = () => {
        localStorage.removeItem(STORAGE_KEY);
        setData(null);
        setKey('');
        setError(null);
        setNotice(null);
        setOtp({ code: '------', status: 'idle', message: '' });
        setOtpLoading(false);
        setOtpCooldown(0);
    };

    const fmtDate = (s) => {
        if (!s) return '—';
        const d = new Date(s.replace(' ', 'T'));
        return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString();
    };

    // A real fetched code (not a placeholder/status) is click-to-copy.
    const OTP_PLACEHOLDERS = ['------', 'Wait...', 'No fresh code', 'Fail', 'Error'];
    const hasOtpCode = otp.status === 'success' && !!otp.code && !OTP_PLACEHOLDERS.includes(otp.code);

    return (
        <div className="redeem-page">
            <div className="redeem-theme-toggle"><ThemeToggle compact /></div>

            <div className={`redeem-card${
                data ? (data.canChange === false ? ' account-valid' : data.canChange === true ? ' account-invalid' : '') : ''
            }`}>
                {restoring ? (
                    <div className="redeem-restoring">
                        <div className="redeem-icon"><Icon name="key" size={30} /></div>
                        <p className="redeem-subtitle">Restoring your session…</p>
                    </div>
                ) : !data ? (
                    <>
                        <div className="redeem-icon"><Icon name="key" size={30} /></div>
                        <h1 className="redeem-title">Redeem your key</h1>
                        <p className="redeem-subtitle">Enter your key to get your account.</p>

                        <form onSubmit={handleRedeem}>
                            <input
                                className="form-input redeem-input"
                                type="text"
                                value={key}
                                onChange={(e) => setKey(e.target.value)}
                                placeholder="Enter your key"
                                autoComplete="off"
                                autoFocus
                            />
                            <button type="submit" className="btn btn-primary redeem-submit" disabled={loading || !key.trim()}>
                                {loading ? 'Checking…' : 'Redeem'}
                            </button>
                        </form>

                        {error && (
                            <div className="alert alert-error redeem-alert">
                                <span><Icon name="alert" size={16} /></span>
                                <div>{error}</div>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="redeem-result">
                        <div className="redeem-result-head">
                            {data.canChange === true
                                ? <span className="badge badge-danger">Account Invalid</span>
                                : <span className="badge badge-success">Active</span>}
                            <button className="btn btn-secondary redeem-change-key" onClick={reset}>Use another key</button>
                        </div>

                        <div className="redeem-account">
                            <div className="redeem-field">
                                <span className="redeem-field-label">Email</span>
                                <div className="redeem-field-value">
                                    <code>{data.account?.email}</code>
                                    <button className="btn btn-secondary redeem-copy" onClick={() => copy(data.account?.email, 'Email')}>
                                        <Icon name="clipboard" size={14} /> Copy
                                    </button>
                                </div>
                            </div>
                            <div className="redeem-field">
                                <span className="redeem-field-label">Password</span>
                                <div className="redeem-field-value">
                                    <code>{data.account?.password}</code>
                                    <button className="btn btn-secondary redeem-copy" onClick={() => copy(data.account?.password, 'Password')}>
                                        <Icon name="clipboard" size={14} /> Copy
                                    </button>
                                </div>
                            </div>
                            {data.account?.info && (
                                <div className="redeem-field">
                                    <span className="redeem-field-label">Notes</span>
                                    <div className="redeem-field-value"><span>{data.account.info}</span></div>
                                </div>
                            )}
                            {data.otpEnabled !== false && (
                                <div className="redeem-field">
                                    <span className="redeem-field-label">Email Verification Code</span>
                                    <div className="redeem-otp-area">
                                        <div
                                            className={`redeem-otp-code ${otp.status} ${hasOtpCode ? 'is-copyable' : ''}`}
                                            onClick={hasOtpCode ? () => copy(otp.code, 'Code') : undefined}
                                            title={hasOtpCode ? 'Click to copy' : undefined}
                                            role={hasOtpCode ? 'button' : undefined}
                                            style={hasOtpCode ? { cursor: 'pointer' } : undefined}
                                        >{otp.code}</div>
                                        <button
                                            className="btn btn-primary redeem-otp-button"
                                            onClick={handleGetOtp}
                                            disabled={otpLoading || otpCooldown > 0}
                                        >
                                            {otpLoading ? 'Fetching...' : (otpCooldown > 0 ? `${otpCooldown}s` : 'Get Code')}
                                        </button>
                                    </div>
                                    {hasOtpCode && <div className="redeem-otp-hint">Tap the code to copy it.</div>}
                                    {otp.message && <div className={`redeem-otp-message ${otp.status}`}>{otp.message}</div>}
                                </div>
                            )}
                        </div>

                        {copied && <div className="redeem-copied">{copied} copied</div>}

                        <div className="redeem-meta">
                            <div><span className="redeem-meta-label">Product</span><span>{data.productName}</span></div>
                            <div><span className="redeem-meta-label">Activated</span><span>{fmtDate(data.activateTime)}</span></div>
                            <div><span className="redeem-meta-label">Expires</span><span>{fmtDate(data.expireTime)}</span></div>
                            <div><span className="redeem-meta-label">Days left</span><span>{data.daysLeft ?? '—'}</span></div>
                        </div>

                        <div className="redeem-note">
                            <strong className="redeem-note-title">Operation Tutorial:</strong>
                            <ol className="redeem-note-list">
                                {(Array.isArray(data.tutorial) && data.tutorial.length > 0
                                    ? data.tutorial
                                    : DEFAULT_TUTORIAL
                                ).map((line, i) => <li key={i}>{line}</li>)}
                            </ol>
                        </div>

                        <button className="btn btn-secondary redeem-switch" onClick={handleSwitch} disabled={switching || data.canChange === false}>
                            <Icon name="refresh" size={15} />
                            {switching ? 'Changing account…' : 'Account abnormal? Change account'}
                        </button>
                        {data.canChange === false && (
                            <div className="redeem-switch-note">
                                <span className="redeem-switch-note-icon"><Icon name="info" size={18} /></span>
                                <span>Your account is <strong>active</strong>. You can switch to a new account <strong>only</strong> if it stops working and is marked invalid by our team.</span>
                            </div>
                        )}

                        {notice && <div className="redeem-inline-notice">{notice}</div>}
                    </div>
                )}
            </div>

            <p className="redeem-footer">Need help? Contact your reseller or administrator.</p>
        </div>
    );
}

export default RedeemKey;
